import { useState } from 'react'
import { X, Plus, Pencil, Trash2, Check } from 'lucide-react'
import { api, KbCategory } from '../../lib/api'

interface Props {
  categories: KbCategory[]
  onClose: () => void
  onChanged: () => void
}

const COLOR_PRESETS = [
  '#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6',
  '#6366f1', '#a855f7', '#ec4899', '#14b8a6', '#6b7280',
]

export function KbCategoryManager({ categories, onClose, onChanged }: Props) {
  const [newName, setNewName] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [newColor, setNewColor] = useState('#6366f1')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editColor, setEditColor] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function handleCreate() {
    if (!newName.trim()) return
    setError(null)
    try {
      await api.solutions.kb.categories.create({
        name: newName.trim(),
        description: newDescription.trim() || undefined,
        color: newColor,
      })
      setNewName('')
      setNewDescription('')
      setNewColor('#6366f1')
      onChanged()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create category')
    }
  }

  async function handleUpdate(id: string) {
    setError(null)
    try {
      await api.solutions.kb.categories.update(id, {
        name: editName.trim(),
        description: editDescription.trim() || undefined,
        color: editColor,
      })
      setEditingId(null)
      onChanged()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update category')
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this category? Documents will be uncategorized.')) return
    setError(null)
    try {
      await api.solutions.kb.categories.delete(id)
      onChanged()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete category')
    }
  }

  function startEdit(cat: KbCategory) {
    setEditingId(cat.id)
    setEditName(cat.name)
    setEditDescription(cat.description || '')
    setEditColor(cat.color)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">Manage Categories</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {/* Existing categories */}
          {categories.map(cat => (
            <div key={cat.id} className="flex items-center gap-2 p-2 rounded-lg border border-gray-200">
              {editingId === cat.id ? (
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      {COLOR_PRESETS.map(c => (
                        <button
                          key={c}
                          onClick={() => setEditColor(c)}
                          className={`w-4 h-4 rounded-full transition-transform ${editColor === c ? 'ring-2 ring-offset-1 ring-gray-400 scale-110' : ''}`}
                          style={{ backgroundColor: c }}
                        />
                      ))}
                    </div>
                  </div>
                  <input
                    type="text"
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:border-indigo-500 outline-none"
                    placeholder="Category name"
                  />
                  <input
                    type="text"
                    value={editDescription}
                    onChange={e => setEditDescription(e.target.value)}
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:border-indigo-500 outline-none"
                    placeholder="Description (optional)"
                  />
                  <div className="flex justify-end gap-1">
                    <button
                      onClick={() => setEditingId(null)}
                      className="px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 rounded transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleUpdate(cat.id)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-white bg-indigo-600 hover:bg-indigo-700 rounded transition-colors"
                    >
                      <Check className="w-3 h-3" />
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: cat.color }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{cat.name}</p>
                    {cat.description && (
                      <p className="text-xs text-gray-500 truncate">{cat.description}</p>
                    )}
                  </div>
                  <button
                    onClick={() => startEdit(cat)}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDelete(cat.id)}
                    className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          ))}

          {categories.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-4">No categories yet</p>
          )}

          {error && (
            <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
          )}

          {/* Add new category */}
          <div className="border-t border-gray-200 pt-3 space-y-2">
            <p className="text-xs font-medium text-gray-700">Add New Category</p>
            <div className="flex gap-1 flex-wrap">
              {COLOR_PRESETS.map(c => (
                <button
                  key={c}
                  onClick={() => setNewColor(c)}
                  className={`w-5 h-5 rounded-full transition-transform ${newColor === c ? 'ring-2 ring-offset-1 ring-gray-400 scale-110' : ''}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
            <input
              type="text"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="Category name"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
            />
            <input
              type="text"
              value={newDescription}
              onChange={e => setNewDescription(e.target.value)}
              placeholder="Description (optional)"
              className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:border-indigo-500 outline-none"
            />
            <button
              onClick={handleCreate}
              disabled={!newName.trim()}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg transition-colors"
            >
              <Plus className="w-3 h-3" />
              Add Category
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
